/**
 * @typedef {'1h' | '3h' | '24h' | '7d' | '30d'} TimeRange
 */

/**
 * @typedef {Object} TelemetryItem
 * @property {number} value
 * @property {string|number} starlink_timestamp
 */

import TelemetryChart from "@components/charts/TelemetryChart/TelemetryChart.jsx";
import Dropdown from "@components/common/Dropdown.jsx";
import LoadingSpinner from "@components/loading/LoadingSpinner.jsx";
import { fetchTelemetryData } from "@services/vesselService.js";
import { formatTimestamp } from "@utils/telemetry.js";
import { memo, useEffect, useRef, useState } from "react";

/**
 * @typedef {Object} MetricChartProps
 * @property {string} title
 * @property {string} metricType
 * @property {string} label
 * @property {string} unit
 * @property {number} [multiplier]
 */

const timeRanges = {
	"15m": "Last 15 Minutes",
	"3h": "Last 3 Hours",
	"24h": "Last 24 Hours",
	"7d": "Last 7 Days",
	"30d": "Last 30 Days",
};

const POLL_INTERVAL_MS = 30_000; // 30 seconds
const POLLABLE_RANGES = new Set(["15m", "3h"]);

/**
 * Metric chart container: fetches telemetry data and renders TelemetryChart.
 *
 * @param {MetricChartProps} props
 * @returns {JSX.Element}
 */
const MetricChart = memo(function MetricChart({
	title,
	metricType,
	label,
	unit,
	multiplier,
	deviceId,
	accountNumber,
}) {
	const containerRef = useRef(null);
	const chartDataRef = useRef({ labels: [], values: [], timestamps: [] });

	const [chartData, setChartData] = useState({ labels: [], values: [], timestamps: [] });
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	/** @type {[TimeRange, React.Dispatch<React.SetStateAction<TimeRange>>]} */
	const [timeRange, setTimeRange] = useState("15m");

	useEffect(() => {
		chartDataRef.current = chartData;
	}, [chartData]);

	useEffect(() => {
		const controller = new AbortController();
		let pollId = null;
		let isVisible = false;
		let hasFetchedInitial = false;

		const transformValue = (rawValue) => {
			const base = rawValue * (multiplier ?? 1);
			return label === "Obstruction" ? Math.round(base * 10) / 10 : base;
		};

		const doInitialFetch = async () => {
			if (controller.signal.aborted) return;
			setLoading(true);
			setError(null);
			try {
				const data = await fetchTelemetryData(
					metricType,
					timeRange,
					deviceId,
					accountNumber,
					controller.signal,
				);
				if (controller.signal.aborted) return;

				const sorted = [...data].sort(
					(a, b) => Number(a.starlink_timestamp) - Number(b.starlink_timestamp),
				);
				setChartData({
					labels: sorted.map((i) => formatTimestamp(Number(i.starlink_timestamp), timeRange)),
					values: sorted.map((i) => transformValue(i.value)),
					timestamps: sorted.map((i) => Number(i.starlink_timestamp)),
				});
			} catch (err) {
				if (controller.signal.aborted) return;
				console.error("Error fetching telemetry data:", err);
				const message =
					err && typeof err === "object" && "message" in err
						? String(err.message)
						: "Failed to fetch data";
				setError(message);
			} finally {
				setLoading(false);
			}
		};

		const doPoll = async () => {
			if (controller.signal.aborted || !isVisible) return;
			try {
				const data = await fetchTelemetryData(
					metricType,
					timeRange,
					deviceId,
					accountNumber,
					controller.signal,
				);
				if (controller.signal.aborted || !data?.length) return;

				const current = chartDataRef.current;
				const lastTs = current.timestamps.length
					? current.timestamps[current.timestamps.length - 1]
					: 0;

				const newPoints = data
					.map((i) => ({ value: i.value, ts: Number(i.starlink_timestamp) }))
					.filter((p) => p.ts > lastTs)
					.sort((a, b) => a.ts - b.ts);

				if (newPoints.length === 0) return;

				setError(null);
				setChartData((prev) => ({
					labels: [...prev.labels, ...newPoints.map((p) => formatTimestamp(p.ts, timeRange))],
					values: [...prev.values, ...newPoints.map((p) => transformValue(p.value))],
					timestamps: [...prev.timestamps, ...newPoints.map((p) => p.ts)],
				}));
			} catch (err) {
				if (controller.signal.aborted) return;
				console.error("Polling error:", err);
			}
		};

		const startPolling = () => {
			if (pollId || !POLLABLE_RANGES.has(timeRange)) return;
			pollId = setInterval(doPoll, POLL_INTERVAL_MS);
		};

		const stopPolling = () => {
			if (pollId) {
				clearInterval(pollId);
				pollId = null;
			}
		};

		const observer = new IntersectionObserver(
			([entry]) => {
				isVisible = entry.isIntersecting;
				if (isVisible) {
					if (!hasFetchedInitial) {
						hasFetchedInitial = true;
						void doInitialFetch().then(() => {
							if (!controller.signal.aborted && isVisible) startPolling();
						});
					} else {
						startPolling();
					}
				} else {
					stopPolling();
				}
			},
			{ threshold: 0.1 },
		);

		if (containerRef.current) observer.observe(containerRef.current);

		return () => {
			controller.abort();
			observer.disconnect();
			stopPolling();
		};
	}, [timeRange, metricType, multiplier, deviceId, accountNumber, label]);

	const handleTimeRangeChange = (range) => setTimeRange(range);

	const renderStats = () => {
		const { values } = chartData;
		if (values.length === 0) return null;

		const isKbMb = unit?.toLowerCase() === "kb/mb";

		const resolveDisplay = (val) => {
			if (!isKbMb) {
				const displayValue = label === "Obstruction" ? +val.toFixed(1) : Math.round(val);
				return { displayValue, displayUnit: unit };
			}
			if (val < 1) return { displayValue: Math.round(val * 1000), displayUnit: "kbps" };
			return { displayValue: +val.toFixed(2), displayUnit: "mbps" };
		};

		const min = resolveDisplay(Math.min(...values));
		const max = resolveDisplay(Math.max(...values));
		const last = resolveDisplay(values[values.length - 1]);

		return (
			<p className="mb-4 text-start font-medium text-sm text-white/80 tracking-wide">
				Min {min.displayValue} {min.displayUnit} · Max {max.displayValue} {max.displayUnit} · Last{" "}
				{last.displayValue} {last.displayUnit}
			</p>
		);
	};

	return (
		<div ref={containerRef}>
			<div className="flex flex-col items-start justify-start">
				<h3 className="mt-2 text-start font-bold text-xs uppercase sm:text-sm md:text-base lg:text-lg">
					{title}
				</h3>

				{chartData.values.length > 0 && renderStats()}

				<div className="mt-2 mb-3">
					<Dropdown value={timeRange} options={timeRanges} onChange={handleTimeRangeChange} />
				</div>
			</div>

			{loading ? (
				<div
					style={{
						height: "320px",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<LoadingSpinner />
				</div>
			) : error ? (
				<div
					style={{
						height: "320px",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						background: "#111217",
						color: "rgba(255,255,255,0.7)",
					}}
				>
					No data
				</div>
			) : (
				<TelemetryChart data={chartData} label={label} unit={unit} />
			)}
		</div>
	);
});

export default MetricChart;
